<template>
  <div class="order-view-page">
    <div v-if="loading" class="loading-wrap">
      <van-loading type="spinner" color="#1989fa" />
      <span>加载中...</span>
    </div>

    <template v-else>
      <div class="content">
        <!-- 状态概览卡片 -->
        <div class="overview-card">
          <div class="overview-head">
            <div class="overview-amounts">
              <div class="amount-col">
                <span class="amount-label">合同金额</span>
                <span class="amount-value">¥{{ formatMoney(orderDetail.courseAmount) }}</span>
              </div>
              <div class="amount-divider"></div>
              <div class="amount-col">
                <span class="amount-label">实收</span>
                <span class="amount-value">¥{{ formatMoney(orderDetail.realPay) }}</span>
              </div>
              <div class="amount-divider"></div>
              <div class="amount-col">
                <span class="amount-label">欠费</span>
                <span class="amount-value" :class="{ danger: orderDetail.arrearage > 0 }">
                  ¥{{ formatMoney(orderDetail.arrearage) }}
                </span>
              </div>
            </div>
            <span class="status-badge" :class="'status-' + getStatusType(orderDetail.status)">
              {{ getDictLabel(audit_status, orderDetail.status) }}
            </span>
          </div>
        </div>

        <!-- 学员信息 -->
        <div class="form-card">
          <div class="card-header">
            <van-icon name="user-o" size="16" color="#1989fa" />
            <span class="card-title">学员信息</span>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="info-label">学员姓名</span>
              <span class="info-value">{{ orderDetail.studentName || '-' }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.studentNo">
              <span class="info-label">学号</span>
              <span class="info-value">{{ orderDetail.studentNo }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">缴费年级</span>
              <span class="info-value">{{ getDictLabel(grade_list, orderDetail.grade) }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.studentStatus !== undefined && orderDetail.studentStatus !== null">
              <span class="info-label">学员状态</span>
              <span class="info-value">{{ getDictLabel(student_status, orderDetail.studentStatus) }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.studentSchoolName">
              <span class="info-label">就读学校</span>
              <span class="info-value">{{ orderDetail.studentSchoolName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">所属校区</span>
              <span class="info-value">{{ orderDetail.schoolName || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- 订单信息 -->
        <div class="form-card">
          <div class="card-header">
            <van-icon name="orders-o" size="16" color="#1989fa" />
            <span class="card-title">订单信息</span>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="info-label">收据编号</span>
              <span class="info-value">{{ orderDetail.receiptNo || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">合同编号</span>
              <span class="info-value">{{ orderDetail.contractNo || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">规划师</span>
              <span class="info-value">{{ orderDetail.plannerName || orderDetail.createUserName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">缴费日期</span>
              <span class="info-value">{{ formatDate(orderDetail.payTime) }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.createTime">
              <span class="info-label">提交日期</span>
              <span class="info-value">{{ formatDate(orderDetail.createTime) }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.remark">
              <span class="info-label">备注</span>
              <span class="info-value">{{ orderDetail.remark }}</span>
            </div>
            <div class="info-row" v-if="orderDetail.auditRemark">
              <span class="info-label">驳回原因</span>
              <span class="info-value danger">{{ orderDetail.auditRemark }}</span>
            </div>
          </div>
        </div>

        <!-- 课程信息 -->
        <div class="form-card" v-if="orderDetail.courseTuitionDetailList?.length">
          <div class="card-header">
            <van-icon name="orders-o" size="16" color="#1989fa" />
            <span class="card-title">课程信息</span>
          </div>
          <div class="card-body">
            <!-- 课时汇总 -->
            <div class="hour-summary">
              <div class="hour-item">
                <span class="hour-num">{{ totalBuyHours }}</span>
                <span class="hour-label">购买课时</span>
              </div>
              <div class="hour-item">
                <span class="hour-num success">+{{ totalGiftHours }}</span>
                <span class="hour-label">赠送课时</span>
              </div>
              <div class="hour-item">
                <span class="hour-num">{{ totalHours }}</span>
                <span class="hour-label">总课时</span>
              </div>
            </div>

            <div
              v-for="(course, idx) in orderDetail.courseTuitionDetailList"
              :key="course.orderCourseId || idx"
              class="course-item"
            >
              <div class="course-item-header">
                <div class="course-name-wrap">
                  <span class="course-name">{{ course.courseName }}</span>
                  <span class="course-paytype" v-if="course.paymentType !== undefined && course.paymentType !== null">
                    {{ getDictLabel(order_type, course.paymentType) }}
                  </span>
                </div>
                <span class="course-hours">
                  {{ course.buyClassHourCount }}课时
                  <span v-if="course.giftClassHourCount > 0" class="success">+{{ course.giftClassHourCount }}</span>
                </span>
              </div>
              <div class="course-price">
                <div class="price-row">
                  <span class="price-label">课程原价</span>
                  <span class="price-val">¥{{ formatMoney(course.coursePay) }}</span>
                </div>
                <div class="price-row" v-if="course.discount !== undefined && course.discount !== null && Number(course.discount) < 100">
                  <span class="price-label">课程折扣</span>
                  <span class="price-val">{{ Number(course.discount) / 10 }}折</span>
                </div>
                <div class="price-row" v-if="course.reductionAmount > 0">
                  <span class="price-label">减免金额</span>
                  <span class="price-val success">-¥{{ formatMoney(course.reductionAmount) }}</span>
                </div>
                <div class="price-row total">
                  <span class="price-label">实付金额</span>
                  <span class="price-val highlight">¥{{ formatMoney(course.realPrice) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 支付信息 -->
        <div class="form-card">
          <div class="card-header">
            <van-icon name="credit-pay" size="16" color="#1989fa" />
            <span class="card-title">支付信息</span>
          </div>
          <div class="card-body">
            <div class="pay-summary">
              <div class="pay-summary-row">
                <span>合同总金额</span>
                <span>¥{{ formatMoney(orderDetail.courseAmount) }}</span>
              </div>
              <div class="pay-summary-row">
                <span>优惠金额</span>
                <span class="success">-¥{{ formatMoney(orderDetail.preferentialAmount) }}</span>
              </div>
              <div class="pay-summary-row">
                <span>合同签约金额</span>
                <span>¥{{ formatMoney(orderDetail.shouldPay) }}</span>
              </div>
              <div class="pay-summary-row" v-if="orderDetail.textBookAmount > 0">
                <span>教材费</span>
                <span>¥{{ formatMoney(orderDetail.textBookAmount) }}</span>
              </div>
              <div class="pay-summary-row" v-if="orderDetail.filingAmount > 0">
                <span>建档费</span>
                <span>¥{{ formatMoney(orderDetail.filingAmount) }}</span>
              </div>
              <div class="pay-summary-row total">
                <span>实收金额</span>
                <span>¥{{ formatMoney(orderDetail.realPay) }}</span>
              </div>
            </div>
            <!-- 支付明细 -->
            <div class="pay-detail-list" v-if="orderDetail.payList?.length">
              <div class="pay-detail-title">支付明细</div>
              <div
                v-for="(pay, idx) in orderDetail.payList"
                :key="pay.payId || idx"
                class="pay-detail-item"
              >
                <div class="pay-detail-left">
                  <span class="pay-type">{{ getDictLabel(pay_type, pay.payType) }}</span>
                  <span class="pay-detail-amount">¥{{ formatMoney(pay.payAmount) }}</span>
                </div>
                <div class="pay-serial" v-if="pay.serialNumber">{{ pay.serialNumber }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 附件 -->
        <div class="form-card" v-if="orderDetail.annexVoList?.length">
          <div class="card-header">
            <van-icon name="photo-o" size="16" color="#1989fa" />
            <span class="card-title">附件凭证</span>
          </div>
          <div class="card-body">
            <div class="annex-grid">
              <van-image
                v-for="annex in orderDetail.annexVoList"
                :key="annex.uid"
                :src="annex.url"
                width="80"
                height="80"
                fit="cover"
                radius="8"
                @click="previewImage(annex.url)"
              />
            </div>
          </div>
        </div>

        <div style="height: 20px;"></div>
      </div>
    </template>

    <!-- 底部操作栏 -->
    <div class="bottom-bar" v-if="!loading && hasActions">
      <van-button v-if="canEdit" plain type="primary" size="small" @click="handleUpdate">修改</van-button>
      <van-button v-if="canSubmitAudit" plain type="primary" size="small" @click="handleSubmitAudit">提交审核</van-button>
      <van-button v-if="canAudit" plain type="success" size="small" @click="handleAuditAction">审核</van-button>
      <van-button v-if="canRevert" plain type="warning" size="small" @click="handleRevert">撤回</van-button>
      <van-button v-if="canPay" plain type="danger" size="small" @click="handlePay">支付</van-button>
      <van-button v-if="canContract" plain type="primary" size="small" :loading="contractLoading"
        @click="handleContract">{{ contractSign ? '下载合同' : '生成合同' }}</van-button>
      <van-button v-if="canVoid" plain type="danger" size="small" @click="handleVoid">作废</van-button>
    </div>

    <!-- 合同签署链接弹层：生成后发给家长，家长打开链接手写签署 -->
    <van-popup v-model:show="showContractInvite" position="bottom" round>
      <div class="contract-invite">
        <div class="ci-title">合同签署链接</div>
        <div class="ci-tip">请将链接发送给家长，家长打开后阅读合同并手写签署。
          <span v-if="contractInvite?.expireTime">有效期至 {{ formatDateTime(contractInvite.expireTime) }}，过期后可重新生成。</span>
        </div>
        <div class="ci-warn">合同签署后订单将无法修改，请确认订单信息无误。</div>
        <div class="ci-url">{{ contractInvite?.url }}</div>
        <van-button type="primary" block @click="copyContractUrl">复制链接</van-button>
        <van-button block plain style="margin-top: 8px; margin-left: 0;" @click="showContractInvite = false">关闭</van-button>
      </div>
    </van-popup>

    <!-- 审核操作面板：点击审核后选择通过/不通过 -->
    <van-action-sheet
      v-model:show="showAuditSheet"
      :actions="auditActions"
      cancel-text="取消"
      description="请选择审核结果"
      @select="onAuditSelect"
    />

    <!-- 驳回原因弹窗 -->
    <van-dialog v-model:show="showAuditRemark" title="驳回原因" show-cancel-button @confirm="confirmAuditFail">
      <div class="audit-remark-dialog">
        <van-field v-model="auditRemark" type="textarea" rows="3" autosize placeholder="请输入驳回原因" />
      </div>
    </van-dialog>

    <!-- 支付金额输入弹窗 -->
    <van-dialog v-model:show="showPayAmount" title="支付金额" show-cancel-button @confirm="confirmPayAmount">
      <div class="pay-dialog">
        <div class="pay-field">
          <span class="pay-label">欠费金额</span>
          <span class="pay-value arrearage">¥{{ orderDetail.arrearage }}</span>
        </div>
        <div class="pay-field">
          <span class="pay-label">支付金额</span>
          <div class="pay-input-wrap">
            <span class="pay-prefix">¥</span>
            <input ref="payInputRef" v-model.number="payAmount" type="number" class="pay-input" placeholder="请输入" />
          </div>
        </div>
      </div>
    </van-dialog>

    <!-- 支付二维码弹窗 -->
    <PayQrcodeDialog v-model:show="showPayQrcode" :order-id="payQrcodeOrderId" :amount="payQrcodeAmount"
      :company-id="orderDetail?.companyId" @success="loadDetail()" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showConfirmDialog, showSuccessToast, showFailToast, showImagePreview } from 'vant'
import {
  getViewData, toAudit, revertAudit, delOrder, wxAudit, listContractSigns, inviteContractSign
} from '@/api/order'
import { getUserInfo as getLocalUserInfo, setUserInfo } from '@/utils/authUtils'
import { getUserInfo as fetchUserInfo } from '@/api/auth'
import { useDict, selectDictLabel } from '@/utils/dict'
import { ensureWecomLogin } from '@/utils/wecomAuth'
import PayQrcodeDialog from './components/PayQrcodeDialog.vue'

const route = useRoute()
const router = useRouter()

const {
  audit_status, grade_list, course_subject, pay_type, order_type, student_status
} = useDict('audit_status', 'grade_list', 'course_subject', 'pay_type', 'order_type', 'student_status')

const userInfo = ref(getLocalUserInfo() || {})
function hasPerm(perm) {
  return userInfo.value?.permissions?.includes(perm) || false
}

const loading = ref(true)
const orderDetail = ref({})
const showPayAmount = ref(false)
const payAmount = ref(0)
const payInputRef = ref(null)
// 动态调整支付金额输入框宽度，使其刚好包裹内容，保证与欠费金额右对齐
function adjustInputWidth() {
  nextTick(() => {
    const el = payInputRef.value
    if (!el) return
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.font = getComputedStyle(el).font
    const text = String(el.value || el.placeholder || '')
    const width = ctx.measureText(text).width
    el.style.width = Math.max(width + 2, 24) + 'px'
  })
}
watch(payAmount, () => adjustInputWidth())
const showPayQrcode = ref(false)
const payQrcodeOrderId = ref('')
const payQrcodeAmount = ref(0)
// 审核
const showAuditRemark = ref(false)
const auditRemark = ref('')
// 审核操作面板（点击审核后选择通过/不通过）
const showAuditSheet = ref(false)
const auditActions = [
  { name: '审核通过', value: 1 },
  { name: '不通过', value: 0 }
]

onMounted(async () => {
  await ensureWecomLogin(route.query.code)
  // 通过链接直接进入时，本地可能没有用户权限信息，需要从后端拉取
  let info = getLocalUserInfo() || {}
  if (!info?.permissions?.length) {
    try {
      info = await fetchUserInfo()
      setUserInfo(info)
    } catch (e) {
      console.error('拉取用户信息失败:', e)
    }
  }
  userInfo.value = info || {}
  await loadDetail()
})

async function loadDetail() {
  loading.value = true
  try {
    const res = await getViewData(route.query.id)
    orderDetail.value = res || {}
    // 订单加载完成后同步合同签署状态（博尔思订单）
    loadContractSign()
  } catch (e) {
    orderDetail.value = {}
  } finally {
    loading.value = false
  }
}

// ==================== 电子合同（博尔思订单，极简版：未签=生成签署链接 / 已签=下载PDF） ====================
const contractSign = ref(null)
const contractLoading = ref(false)
const showContractInvite = ref(false)
const contractInvite = ref(null)

async function loadContractSign() {
  contractSign.value = null
  if (!canContract.value || !orderDetail.value.orderId) return
  try {
    // 后端按签署时间倒序返回全部有效记录，取首条即最新
    const res = await listContractSigns(orderDetail.value.orderId)
    contractSign.value = (res && res[0]) || null
  } catch (e) {
    contractSign.value = null
  }
}

async function handleContract() {
  // 已签：直接下载归档PDF（PDF未生成时后端现场兜底补生成）
  if (contractSign.value) {
    const url = (process.env.VUE_APP_BASE_API || '') + '/financial/contract/download/'
      + contractSign.value.signId + '?token=' + encodeURIComponent(contractSign.value.downloadToken) + '&download=1'
    window.open(url)
    return
  }
  // 未签：发起前提醒——家长签署后订单锁定无法修改，确认订单信息无误再生成链接
  try {
    await showConfirmDialog({
      title: '发起合同签署',
      message: '请提醒家长：合同一经签署，订单将无法修改。\n请确认订单信息（课程、金额等）无误后再发起。',
      confirmButtonText: '确认无误，生成链接',
      cancelButtonText: '再检查一下'
    })
  } catch (e) {
    return // 取消发起
  }
  contractLoading.value = true
  try {
    const res = await inviteContractSign(orderDetail.value.orderId)
    contractInvite.value = res || {}
    showContractInvite.value = true
  } catch (e) {
    // 失败提示由拦截器统一处理
  } finally {
    contractLoading.value = false
  }
}

async function copyContractUrl() {
  const url = contractInvite.value?.url || ''
  if (!url) return
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
    } else {
      const ta = document.createElement('textarea')
      ta.value = url
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    showSuccessToast('链接已复制，请发送给家长')
  } catch (e) {
    showFailToast('复制失败，请长按链接文本复制')
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function getDictLabel(dict, value) {
  return selectDictLabel(dict, value)
}

function getStatusType(status) {
  const map = { 0: 'warning', 1: 'primary', 2: 'danger', 3: 'success', 4: 'default', 5: 'primary', 6: 'warning', 7: 'warning' }
  return map[status] || 'default'
}

function formatDate(date) {
  if (!date) return '-'
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatMoney(val) {
  const num = Number(val) || 0
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function previewImage(url) {
  showImagePreview([url])
}

// 课时汇总
const totalBuyHours = computed(() =>
  (orderDetail.value.courseTuitionDetailList || []).reduce((sum, c) => sum + (Number(c.buyClassHourCount) || 0), 0)
)
const totalGiftHours = computed(() =>
  (orderDetail.value.courseTuitionDetailList || []).reduce((sum, c) => sum + (Number(c.giftClassHourCount) || 0), 0)
)
const totalHours = computed(() => totalBuyHours.value + totalGiftHours.value)

// 操作权限判断
const canEdit = computed(() =>
  hasPerm('financial:order:edit') && [0, 2, 6].includes(orderDetail.value.status)
)
const canSubmitAudit = computed(() =>
  hasPerm('financial:order:edit') && [0, 2, 6].includes(orderDetail.value.status)
)
const canAudit = computed(() => {
  const s = orderDetail.value.status
  if (s === 7) return hasPerm('special_audit')
  return hasPerm('financial:order:audit') &&
    ((s === 1 && !orderDetail.value.isSpecialDiscount) || s === 5)
})
const canRevert = computed(() =>
  hasPerm('financial:order:edit') &&
  (orderDetail.value.status === 7 ||
    (orderDetail.value.status === 1 && !orderDetail.value.isSpecialDiscount))
)
const canPay = computed(() =>
  hasPerm('financial:order:edit') &&
  orderDetail.value.arrearage > 0 &&
  // 5万上限仅博尔思（companyId=1，数币监管支付渠道）保留；博思走富友收款码不限此额
  (Number(orderDetail.value.companyId) !== 1 || orderDetail.value.shouldPay <= 50000) &&
  ![3, 4].includes(orderDetail.value.status)
)
const canVoid = computed(() =>
  hasPerm('financial:orderRefund:remove') && ![3, 4].includes(orderDetail.value.status)
)
// 电子合同：对齐PC端入口条件，仅博尔思订单展示（生成签署链接/下载PDF）
const canContract = computed(() =>
  hasPerm('financial:order:detail') && Number(orderDetail.value.companyId) === 1
)
const hasActions = computed(() =>
  canEdit.value || canSubmitAudit.value || canAudit.value || canRevert.value || canPay.value || canContract.value || canVoid.value
)

function handleUpdate() {
  router.push({ path: '/orderForm', query: { id: orderDetail.value.orderId } })
}

async function handleSubmitAudit() {
  try {
    await showConfirmDialog({ title: '', message: '确认提交审核？' })
    await toAudit(orderDetail.value.orderId)
    showSuccessToast('提交成功')
    loadDetail()
  } catch (e) { /* cancel */ }
}

async function handleRevert() {
  try {
    await showConfirmDialog({ title: '', message: '确认撤回审核？' })
    await revertAudit(orderDetail.value.orderId)
    showSuccessToast('撤回成功')
    loadDetail()
  } catch (e) { /* cancel */ }
}

function handleAuditAction() {
  showAuditSheet.value = true
}

function onAuditSelect(action) {
  showAuditSheet.value = false
  if (action.value === 1) {
    handleAuditPass()
  } else {
    handleAuditFail()
  }
}

function handleAuditPass() {
  showConfirmDialog({ title: '', message: '确认该订单审核通过？' })
    .then(async () => {
      await wxAudit({ orderId: orderDetail.value.orderId, auditResult: 1, remark: '' })
      showSuccessToast('审核通过')
      loadDetail()
    })
    .catch(() => { /* cancel */ })
}

function handleAuditFail() {
  auditRemark.value = ''
  showAuditRemark.value = true
}

async function confirmAuditFail() {
  if (!auditRemark.value) {
    showFailToast('请输入驳回原因')
    return
  }
  await wxAudit({ orderId: orderDetail.value.orderId, auditResult: 0, remark: auditRemark.value })
  showSuccessToast('已驳回')
  loadDetail()
}

async function handleVoid() {
  try {
    await showConfirmDialog({ title: '', message: '确认作废该订单？' })
    await delOrder(orderDetail.value.orderId)
    showSuccessToast('作废成功')
    router.back()
  } catch (e) { /* cancel */ }
}

function handlePay() {
  payAmount.value = orderDetail.value.arrearage
  showPayAmount.value = true
}

async function confirmPayAmount() {
  if (!payAmount.value || payAmount.value <= 0) {
    showFailToast('支付金额必须大于0')
    return
  }
  if (payAmount.value > orderDetail.value.arrearage) {
    showFailToast('支付金额不能超过欠费金额')
    return
  }
  showPayAmount.value = false
  payQrcodeOrderId.value = orderDetail.value.orderId
  payQrcodeAmount.value = payAmount.value
  showPayQrcode.value = true
}
</script>

<style scoped>
.order-view-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f5f6f8;
  padding-bottom: 70px;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 10px;
  color: #969799;
  font-size: 14px;
}

.content {
  padding: 12px;
}

/* 状态概览卡片 */
.overview-card {
  background: linear-gradient(135deg, #1989fa, #0066cc);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  color: #fff;
}
.overview-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: rgba(255,255,255,0.2);
  color: #fff;
  backdrop-filter: blur(4px);
  white-space: nowrap;
  flex-shrink: 0;
}
.status-badge.status-success {
  background: rgba(7,193,96,0.3);
}
.status-badge.status-danger {
  background: rgba(238,10,36,0.3);
}
.status-badge.status-warning {
  background: rgba(255,153,0,0.3);
}
.status-badge.status-primary {
  background: rgba(255,255,255,0.25);
}
.overview-amounts {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}
.amount-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
}
.amount-label {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
}
.amount-value {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}
.amount-value.danger {
  color: #ffd21a;
}
.amount-divider {
  width: 1px;
  height: 28px;
  background: rgba(255,255,255,0.25);
}

/* 卡片 */
.form-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 16px 10px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}
.card-body {
  padding: 0 16px 14px;
}

/* 信息行 */
.info-row {
  display: flex;
  padding: 9px 0;
  font-size: 13px;
  border-bottom: 1px solid #f5f6f8;
}
.info-row:last-child {
  border-bottom: none;
}
.info-label {
  color: #969799;
  width: 72px;
  flex-shrink: 0;
}
.info-value {
  color: #323233;
  flex: 1;
  word-break: break-all;
}
.info-value.danger {
  color: #ee0a24;
}

/* 课时汇总 */
.hour-summary {
  display: flex;
  background: #f0f7ff;
  border-radius: 10px;
  padding: 12px 0;
  margin-bottom: 12px;
}
.hour-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
}
.hour-item:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 24px;
  background: #d0e8ff;
}
.hour-num {
  font-size: 20px;
  font-weight: 700;
  color: #1989fa;
}
.hour-num.success {
  color: #07c160;
}
.hour-label {
  font-size: 11px;
  color: #969799;
}

/* 课程 */
.course-item {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
}
.course-item:last-child {
  margin-bottom: 0;
}
.course-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}
.course-name-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.course-name {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.course-paytype {
  flex-shrink: 0;
  font-size: 11px;
  color: #1989fa;
  background: rgba(25, 137, 250, 0.1);
  border: 1px solid rgba(25, 137, 250, 0.3);
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 1.5;
}
.course-hours {
  font-size: 12px;
  color: #969799;
  flex-shrink: 0;
}
.course-hours .success {
  color: #07c160;
  font-weight: 500;
}
.course-price {
  border-top: 1px dashed #ebedf0;
  padding-top: 8px;
}
.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
  font-size: 12px;
}
.price-label {
  color: #969799;
}
.price-val {
  color: #323233;
  font-weight: 500;
}
.price-val.success {
  color: #07c160;
}
.price-row.total {
  padding-top: 6px;
  margin-top: 2px;
  border-top: 1px solid #f2f3f5;
}
.price-row.total .price-label {
  color: #646566;
  font-weight: 500;
}
.price-val.highlight {
  color: #1989fa;
  font-weight: 700;
  font-size: 14px;
}

/* 支付汇总 */
.pay-summary {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 10px 14px;
}
.pay-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 13px;
  color: #646566;
}
.pay-summary-row.success {
  color: #07c160;
}
.pay-summary-row.total {
  border-top: 1px dashed #dcdee0;
  margin-top: 4px;
  padding-top: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

/* 支付明细 */
.pay-detail-list {
  margin-top: 12px;
}
.pay-detail-title {
  font-size: 13px;
  color: #969799;
  margin-bottom: 8px;
}
.pay-detail-item {
  padding: 8px 0;
  border-bottom: 1px solid #f5f6f8;
}
.pay-detail-item:last-child {
  border-bottom: none;
}
.pay-detail-left {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
.pay-type {
  font-size: 13px;
  font-weight: 600;
  color: #323233;
}
.pay-serial {
  font-size: 11px;
  color: #c8c9cc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pay-detail-amount {
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}

/* 附件 */
.annex-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 合同签署链接弹层 */
.contract-invite {
  padding: 20px 16px calc(16px + env(safe-area-inset-bottom));
}
.ci-title {
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
}
.ci-tip {
  font-size: 13px;
  color: #969799;
  line-height: 1.6;
  margin-bottom: 10px;
}

.ci-warn {
  font-size: 12px;
  color: #ee9d2d;
  line-height: 1.6;
  margin-bottom: 10px;
}
.ci-url {
  font-size: 13px;
  color: #1989fa;
  word-break: break-all;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  user-select: text;
  -webkit-user-select: text;
}

/* 底部操作栏 */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  padding: 10px 12px calc(10px + constant(safe-area-inset-bottom));
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  display: flex;
  gap: 8px;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
  z-index: 100;
}
.bottom-bar .van-button {
  flex: 1;
}

.audit-remark-dialog {
  padding: 10px 0;
}

.pay-amount-dialog {
  padding: 10px 0;
}
.pay-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 14px;
}
.pay-row .danger {
  color: #ee0a24;
  font-weight: 600;
}

/* 补缴欠费弹窗 - 与列表页对齐 */
.pay-dialog {
  padding: 4px 20px 8px;
}
.pay-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
}
.pay-field + .pay-field {
  border-top: 1px solid #f2f3f5;
}
.pay-label {
  font-size: 14px;
  color: #646566;
  flex-shrink: 0;
}
.pay-value {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}
.pay-value.arrearage {
  color: #ee0a24;
}
.pay-input-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 4px 0 4px 10px;
  min-width: 100px;
}
.pay-input-wrap:focus-within {
  border-color: #1989fa;
  background: #fff;
}
.pay-prefix {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}
.pay-input {
  width: auto;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  text-align: left;
  outline: none;
  padding: 2px 0;
  -moz-appearance: textfield;
}
.pay-input::-webkit-outer-spin-button,
.pay-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.pay-input::placeholder {
  color: #c8c9cc;
  font-weight: 400;
}
</style>
